// /en/company/documents/$group — Wave 71: EN deep-dive per corporate document family
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import { COMPANY_DOC_GROUPS, type CompanyDocGroup } from "@/data/company-doc-groups";
import { APOSTILLE_STATUS } from "@/data/apostille-status";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/company/documents/$group")({
  loader: async ({ params }) => {
    const { COMPANY_DOC_GROUPS } = await import("@/data/company-doc-groups");
    const group = COMPANY_DOC_GROUPS.find((g) => g.slug === params.group);
    if (!group) throw notFound();
    return {
      slug: group.slug,
      title: group.titleEn,
      summary: group.summaryEn,
      faqs: group.faqsEn.map((f) => ({ q: f.q, a: f.a })),
    };
  },
  head: ({ params, loaderData }) => {
    const g = loaderData;
    if (!g) return { meta: [{ title: "Not found" }, { name: "robots", content: "noindex" }] };
    const path = `/en/company/documents/${params.group}`;
    const url = `${SITE}${path}`;
    const thUrl = `${SITE}/company/documents/${params.group}`;
    const title = `${g.title} | NYC`;
    const desc = g.summary.slice(0, 155);
    return {
      meta: [
        { title: ctaTitle(title, path, "en") },
        { name: "description", content: ctaDesc(desc, path, "en") },
        { property: "og:title", content: ctaTitle(title, path, "en") },
        { property: "og:description", content: ctaDesc(desc, path, "en") },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_US" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "en", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: thUrl },
        { rel: "alternate", hrefLang: "x-default", href: thUrl },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "@id": `${url}#breadcrumb`,
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Corporate documents", item: `${SITE}/en/company/documents` },
              { "@type": "ListItem", position: 2, name: g.title, item: url },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "@id": `${url}#faq`,
            inLanguage: "en",
            mainEntity: g.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: CompanyDocGroupEnPage,
});

function CompanyDocGroupEnPage() {
  const { slug } = Route.useLoaderData();
  const g = COMPANY_DOC_GROUPS.find((x) => x.slug === slug) as CompanyDocGroup;
  const others = COMPANY_DOC_GROUPS.filter((x) => x.slug !== g.slug);

  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">
        <Link to="/en/company/documents" className="hover:underline">
          Corporate documents
        </Link>{" "}
        / {g.titleEn}
      </nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{g.titleEn}</h1>
      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        {g.summaryEn}
      </p>

      <aside className="mt-6 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">
        <strong>Thailand&apos;s Apostille status:</strong> the instrument of accession was deposited on{" "}
        {APOSTILLE_STATUS.accessionDepositEn} and the Convention enters into force for Thailand on{" "}
        {APOSTILLE_STATUS.entryIntoForceEn}. Until then, Thai corporate documents used abroad still pass through the
        Department of Consular Affairs and the destination embassy in the traditional order.{" "}
        <a href={APOSTILLE_STATUS.sourceUrl} target="_blank" rel="noopener noreferrer nofollow" className="underline">
          Source: HCCH
        </a>
      </aside>

      <div className="mt-8 space-y-4 text-base leading-8">
        {g.bodyEn.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      <h2 className="mt-10 text-2xl font-semibold tracking-tight">Documents to prepare</h2>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-7">
        {g.documentsEn.map((d) => (
          <li key={d}>{d}</li>
        ))}
      </ul>

      <h2 className="mt-8 text-2xl font-semibold tracking-tight">Order of steps</h2>
      <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm leading-7">
        {g.chainEn.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ol>

      <h2 className="mt-8 text-2xl font-semibold tracking-tight">Pre-filing checklist</h2>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-7">
        {g.checklistEn.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>

      <h2 className="mt-8 text-2xl font-semibold tracking-tight">Frequently asked questions</h2>
      <div className="mt-2 space-y-3">
        {g.faqsEn.map((f) => (
          <div key={f.q} className="rounded-lg border border-border p-4">
            <p className="font-medium voice-faq-q">{f.q}</p>
            <p className="mt-1 text-sm leading-7 text-muted-foreground voice-faq-a">{f.a}</p>
          </div>
        ))}
      </div>

      <p className="mt-8 rounded-lg border border-border bg-muted/30 p-4 text-sm leading-7">
        Want our team to review the corporate file before submission? Contact LINE @nycli, phone +66-83-249-4999 or
        email nycli@ilc.ltd and we will confirm the steps and a realistic timeline for your case.
      </p>

      <h2 className="mt-10 text-xl font-semibold tracking-tight">Related cases</h2>
      <ul className="mt-2 grid gap-2 sm:grid-cols-2">
        {others.map((o) => (
          <li key={o.slug}>
            <Link to="/en/company/documents/$group" params={{ group: o.slug }} className="text-primary hover:underline">
              {o.titleEn}
            </Link>
          </li>
        ))}
      </ul>
          <ServiceAuthorityBlock topic="company" locale="en" />
    </main>
  );
}
